import React, { useEffect, useRef, useState } from "react";
import { string } from "prop-types";
import { MobileView } from "react-device-detect";
import { DfpComponent } from "../../ads/dfp-component";
import { get } from "lodash";
import { useSelector } from "react-redux";

import "./sticky-ad.m.css";

const getStoryContentId = (story = {}) => story["content-id"] || story["story-content-id"] || story.id;
const getStoryAdId = (story = {}) => story["public-identifier"] || getStoryContentId(story);
const isMobileViewport = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

const updateSlotTargeting = (slot, story) => {
  const storyId = getStoryAdId(story);
  const sectionSlug = get(story, ["sections", 0, "slug"], "");

  if (storyId) {
    slot.setTargeting("Article_ID", storyId);
  } else {
    slot.clearTargeting("Article_ID");
  }

  if (sectionSlug) {
    slot.setTargeting("Section_Name", sectionSlug);
  } else {
    slot.clearTargeting("Section_Name");
  }
};

const refreshStickyAdSlot = (slotId, story, onRefresh, retryCount = 0) => {
  if (!window.googletag?.cmd) return;

  window.googletag.cmd.push(() => {
    if (!window.googletag.pubadsReady) {
      if (retryCount < 3) {
        window.setTimeout(() => refreshStickyAdSlot(slotId, story, onRefresh, retryCount + 1), 500);
      }
      return;
    }

    const stickySlot = window.googletag
      .pubads()
      .getSlots()
      .find((slot) => slot.getSlotElementId() === slotId);

    if (!stickySlot) {
      if (retryCount < 3) {
        window.setTimeout(() => refreshStickyAdSlot(slotId, story, onRefresh, retryCount + 1), 500);
      }
      return;
    }

    updateSlotTargeting(stickySlot, story);
    window.googletag.pubads().refresh([stickySlot]);
    onRefresh?.();
  });
};

const StickyAd = ({ pageType }) => {
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});
  let pageConfig = get(qtState, ["config", "ads-config", "ad_slots", pageType], {});
  if (pageType === "story-page" && qtState?.data?.story?.["story-template"] !== "text") {
    pageConfig = get(qtState, ["config", "ads-config", "ad_slots", qtState?.data?.story?.["story-template"]], {});
  } else if (qtState?.data?.section?.slug.includes("photos")) {
    pageConfig = get(qtState, ["config", "ads-config", "ad_slots", "photo-listing"], {});
  } else if (qtState?.data?.section?.slug.includes("videos")) {
    pageConfig = get(qtState, ["config", "ads-config", "ad_slots", "video-listing"], {});
  }
  const stickyAdUnit = get(pageConfig, ["mobile", "sticky_ad", "ad_unit"], "");
  const [adComponent, setAdComponent] = useState();
  const lastRefreshedStoryIdRef = useRef(getStoryContentId(qtState?.data?.story));

  useEffect(() => {
    setAdComponent(
      stickyAdUnit && (
        <MobileView>
          <DfpComponent
            id={`${pageType}-sticky_ad`}
            path={`/${networkId}/${stickyAdUnit}`}
            adSlotConfigName={"320x50_ad"}
            showText={false}
          />
        </MobileView>
      )
    );
  }, []);

  useEffect(() => {
    if (!stickyAdUnit) {
      return undefined;
    }

    const handleActiveStoryChange = (event) => {
      const { story, isPerpetualStory } = event.detail || {};
      const storyContentId = getStoryContentId(story);

      if (!isPerpetualStory || !storyContentId || !isMobileViewport()) return;
      if (lastRefreshedStoryIdRef.current === storyContentId) return;

      refreshStickyAdSlot(`${pageType}-sticky_ad`, story, () => {
        lastRefreshedStoryIdRef.current = storyContentId;
      });
    };

    window.addEventListener("perpetual-story-active", handleActiveStoryChange);
    return () => {
      window.removeEventListener("perpetual-story-active", handleActiveStoryChange);
    };
  }, [pageType, stickyAdUnit]);

  return <div styleName="sticky-ad">{adComponent}</div>;
};
StickyAd.propTypes = {
  pageType: string,
};
export default StickyAd;
